import * as React from 'react';
import { Box, StackLayout, TextAction } from '@leagueplatform/genesis-core';
import { NavLink, useLocation } from 'react-router-dom';
import { composePaths, Actions } from '@jsonforms/core';
import { useJsonForms } from '@jsonforms/react';
import type { AnnotatedJsonSchema } from '@web-config-app/core';
import { getFirstUseableLabel } from '@web-config-app/core';
import { useLocales, regenerateGeneratedIds } from '@web-config-app/core-react';
import { FlowViewer } from '@web-config-app/flow-builder';
import { get } from 'lodash-es';
import {
  createDefaultValueForSchema,
  EntityFormArrayControl,
  ArrayAddButton,
  CombinatorArrayAddActionMenu,
  NumberIcon,
  ArrayActionButtons,
  CompoundComplexHeading,
  EmptyArrayState,
  getDataPointValue,
  useObjectCardLink,
  useArrayControlCombinatorHelpers,
  addCombinatorOptionToArray,
  useMoveArrayItems,
  useEntityDetailsProps,
  useLocalizedArrayOperations,
  moveItemToIndex,
  useJsonFormsErrorsForPath,
} from '@web-config-app/entity-form';
import type {
  ArrayControl,
  EntityFormArrayControlProps,
  ComplexObjectArrayControlItem,
  SortableItemRenderProps,
} from '@web-config-app/entity-form';
import {
  MoveArrayItemsCheckboxContainer,
  MoveArrayItems,
} from '../move-array-items/move-array-items.component';
import { FlowNodeArrayItem } from './flow-node-array-item.component';
import { useFlowArray } from './use-flow-array.hook';

/**
 * Check if the current array schema has the arrayMove control annotation
 */
const hasArrayMoveControl = (schema: AnnotatedJsonSchema) =>
  schema?.['x-entity-control']?.type === 'arrayMove';

/**
 * This control renders for Arrays of complex objects. A complex object is an object that has properties that are also
 * objects. Another way to describe this would be to say the object has nested objects inside of it.
 */

export const ArrayOfComplexObjectsControlContents = ({
  path,
  schema,
  rootSchema,
  data,
  id,
  banner,
  hint,
  label,
  addItem,
  removeItems,
  moveDown,
  moveUp,
  arrayAddLabel,
  itemLabel: defaultItemLabel,
  itemLabelPropertyRef,
  statusMessage,
  inputStatus,
  enabled,
  combinatorProperties,
  localized,
  hideLabel,
  renderers,
  cells,
  createSortableId,
  SortableItem,
  SortableWrapper,
  registerAfterReorderCallback,
}: EntityFormArrayControlProps) => {
  const ctx = useJsonForms();
  const { dispatch } = ctx;
  const { entity, formPath } = useEntityDetailsProps();
  const { locales } = useLocales();
  const isLocalizable =
    localized && locales && locales.length > 1 && entity.localizable;
  const { state: locationState } = useLocation();

  // localized array operations will only be used if isLocalizable is true
  const {
    handleLocalizedMoveUp,
    handleLocalizedMoveDown,
    handleLocalizedRemoveItem,
    addLocalizedArrayItem,
    handleLocalizedDuplicateItem,
    handleLocalizedMoveToPosition,
  } = useLocalizedArrayOperations({
    path,
    formPath,
    placeholderValue: {},
  });

  const handleLocalizedMoveToPositionRef = React.useRef(
    handleLocalizedMoveToPosition,
  );
  const isLocalizableRef = React.useRef(isLocalizable);

  React.useEffect(() => {
    handleLocalizedMoveToPositionRef.current = handleLocalizedMoveToPosition;
    isLocalizableRef.current = isLocalizable;
  }, [handleLocalizedMoveToPosition, isLocalizable]);

  React.useEffect(() => {
    registerAfterReorderCallback((fromIndex: number, toIndex: number) => {
      if (isLocalizableRef.current) {
        handleLocalizedMoveToPositionRef.current({ fromIndex, toIndex });
      }
    });
  }, [registerAfterReorderCallback]);

  const handleDuplicateItem = React.useCallback(
    (itemData: any, index: number) => () => {
      // Resolve oneOf discriminator if present
      const annotatedSchema = schema as AnnotatedJsonSchema;
      let resolvedSchema = annotatedSchema;
      if (
        annotatedSchema.oneOf &&
        annotatedSchema.discriminator?.propertyName
      ) {
        const discriminatorProp = annotatedSchema.discriminator.propertyName;
        const discriminatorValue = itemData[discriminatorProp];

        const oneOfSchemas: AnnotatedJsonSchema[] = annotatedSchema.oneOf;
        if (discriminatorValue) {
          const matchedSchema = oneOfSchemas.find(
            (oneOfSchema: AnnotatedJsonSchema) => {
              const enumValues: string[] = get(
                oneOfSchema,
                `properties.${discriminatorProp}.enum`,
                [],
              );
              return enumValues.includes(discriminatorValue);
            },
          );

          if (matchedSchema) {
            // Use the matched schema branch
            resolvedSchema = matchedSchema as AnnotatedJsonSchema;
          }
        }
      }

      const dupe = regenerateGeneratedIds(itemData, resolvedSchema);
      addItem(path, dupe)();
      if (isLocalizable) {
        handleLocalizedDuplicateItem({ index });
      }
    },
    [addItem, handleLocalizedDuplicateItem, isLocalizable, path, schema],
  );

  const handleRemoveItem = React.useCallback(
    (itemPath: string, index: any) => () => {
      removeItems(itemPath, [index])();
      if (isLocalizable) {
        handleLocalizedRemoveItem({ index });
      }
    },
    [handleLocalizedRemoveItem, isLocalizable, removeItems],
  );

  const handleOnMoveUp = React.useCallback(
    (itemPath: string, index: number) => () => {
      moveUp(itemPath, index)();
      if (isLocalizable) {
        handleLocalizedMoveUp({ index });
      }
    },
    [handleLocalizedMoveUp, isLocalizable, moveUp],
  );

  const handleOnMoveDown = React.useCallback(
    (itemPath: string, index: number) => () => {
      moveDown(itemPath, index)();
      if (isLocalizable) {
        handleLocalizedMoveDown({ index });
      }
    },
    [handleLocalizedMoveDown, isLocalizable, moveDown],
  );

  const handleMoveToPosition = React.useCallback(
    (itemPath: string, fromIndex: number) => (toIndex: number) => {
      const newArray = moveItemToIndex(data, fromIndex, toIndex);
      dispatch?.(Actions.update(itemPath, () => newArray));

      // Handle localized arrays if needed
      if (isLocalizable) {
        handleLocalizedMoveToPosition({ fromIndex, toIndex });
      }
    },
    [data, dispatch, handleLocalizedMoveToPosition, isLocalizable],
  );

  const handleCreateDefaultValue = React.useCallback(() => {
    if (isLocalizable) {
      addLocalizedArrayItem();
    }
    return createDefaultValueForSchema(schema) || {};
  }, [addLocalizedArrayItem, isLocalizable, schema]);

  const {
    combinatorActionMenuItems,
    getSelectedSubSchema,
    getLabelForSelectedSubSchema,
  } = useArrayControlCombinatorHelpers(combinatorProperties);

  const { getLinkForObjectCard } = useObjectCardLink();

  const itemsWithLabels: ComplexObjectArrayControlItem[] = React.useMemo(() => {
    const items = Array.isArray(data) ? data : [];
    return items.map((item: any, index: number) => {
      const { combinatorDiscriminator, combinatorSubSchemaMap } =
        combinatorProperties ?? {};
      const itemSchema = (
        combinatorDiscriminator
          ? combinatorSubSchemaMap?.[item[combinatorDiscriminator]]
          : schema.items
      ) as AnnotatedJsonSchema;
      const propertyRef =
        itemLabelPropertyRef ?? itemSchema?.['x-entity-label']?.propertyRef;

      const itemLabelDataValue = propertyRef
        ? getDataPointValue(item, propertyRef)
        : null;

      const itemNumber = index + 1;

      const isLastItem = items.length === itemNumber;
      const uniqueId = `${id}-${index}`;
      const selectedSubSchema = getSelectedSubSchema(item);
      const subSchemaLabel = getLabelForSelectedSubSchema(selectedSubSchema);
      const childPath = composePaths(path, `${index}`);
      const to = getLinkForObjectCard(childPath);

      return {
        isLastItem,
        itemNumber,
        uniqueId,
        to,
        itemLabel: getFirstUseableLabel(
          itemLabelDataValue,
          subSchemaLabel,
          defaultItemLabel,
        ),
        itemData: data[index],
        ...item,
      };
    });
  }, [
    data,
    defaultItemLabel,
    itemLabelPropertyRef,
    id,
    getSelectedSubSchema,
    getLabelForSelectedSubSchema,
    path,
    getLinkForObjectCard,
    combinatorProperties,
    schema.items,
  ]);

  const arrayItemsEmpty = itemsWithLabels?.length === 0;

  const { arrayItemHasError } = useJsonFormsErrorsForPath(path);

  /**
   * Check if the current array schema has the arrayMove control annotation.
   * If it does, we will render the selectable move array items component.
   */
  const hasArrayMoveControlAnnotation = hasArrayMoveControl(schema);

  const {
    isSelectionMode,
    selectedItems,
    handleToggleSelection,
    handleMoveClick,
    handleCancelMove,
    handleSelectDestination,
    handleSelectAll,
    isMoveItemsModalOpen,
    setIsMoveItemsModalOpen,
    handleMoveItems,
  } = useMoveArrayItems<ComplexObjectArrayControlItem>({
    items: itemsWithLabels,
    getItemLabel: (item) => item.itemLabel,
    getItemData: (item) => item.itemData,
    localized,
  });
  const disableActions = !enabled || isSelectionMode;

  const {
    nodeTypeLabelMap,
    isFlowNodeControlAnnotation,
    nodeNextKeyMap,
    addStartNode,
    addEndNode,
  } = useFlowArray(schema);

  // Track which item has its drawer open
  const [activeDrawerIndex, setActiveDrawerIndex] = React.useState<
    number | null
  >(null);

  const handleDrawerStateChange = React.useCallback(
    (index: number, isOpen: boolean) => {
      setActiveDrawerIndex(isOpen ? index : null);
    },
    [],
  );

  return (
    <>
      <CompoundComplexHeading
        label={label}
        banner={banner}
        hint={hint}
        statusMessage={statusMessage}
        inputStatus={inputStatus}
        hideLabel={hideLabel}
      />
      {arrayItemsEmpty ? (
        <EmptyArrayState item={label} />
      ) : (
        <>
          <StackLayout
            orientation="vertical"
            horizontalAlignment="stretch"
            spacing="$one"
            css={{
              marginBlockEnd: '$one',
            }}
          >
            {isFlowNodeControlAnnotation && (
              <FlowViewer
                data={data}
                nodeTypeLabelMap={nodeTypeLabelMap}
                nodeNextKeyMap={nodeNextKeyMap}
                addStartNode={addStartNode}
                addEndNode={addEndNode}
              />
            )}
            {hasArrayMoveControlAnnotation && (
              <MoveArrayItems
                isSelectionMode={isSelectionMode}
                selectedItems={selectedItems}
                handleSelectAll={handleSelectAll}
                handleMoveClick={handleMoveClick}
                handleCancelMove={handleCancelMove}
                handleSelectDestination={handleSelectDestination}
                handleMoveItems={handleMoveItems}
                isMoveItemsModalOpen={isMoveItemsModalOpen}
                setIsMoveItemsModalOpen={setIsMoveItemsModalOpen}
                items={itemsWithLabels}
                getItemLabel={(item) => item.itemLabel}
                schema={schema}
              />
            )}
          </StackLayout>
          <SortableWrapper>
            <StackLayout
              role="list"
              aria-label={label}
              css={{
                marginBlockStart: '$threeQuarters',
                border: '$borderWidths$thin solid $onSurfaceBorderSubdued',
                borderRadius: '$medium',
                paddingInlineStart: '$none',
              }}
            >
              {itemsWithLabels.map(
                (
                  { itemLabel, itemNumber, isLastItem, uniqueId, to, itemData },
                  index: number,
                ) => {
                  const sortableId = createSortableId(index);

                  return (
                    <SortableItem
                      key={uniqueId ?? sortableId}
                      id={sortableId}
                      disabled={!enabled || isSelectionMode}
                      containerCss={{
                        width: '100%',
                        paddingBlock: '$one',
                        paddingInline: '$one',
                        marginBlockStart: 0,
                        borderBottom: `${
                          isLastItem
                            ? 'none'
                            : '$borderWidths$thin solid $onSurfaceBorderSubdued'
                        }`,
                        '&:first-of-type': {
                          borderRadius: '$medium $medium 0 0',
                        },
                        '&:last-of-type': {
                          borderRadius: '0 0 $medium $medium',
                        },
                        '&:first-of-type:last-of-type': {
                          borderRadius: '$medium',
                        },
                        backgroundColor:
                          activeDrawerIndex === index
                            ? '$surfaceBackgroundTertiary'
                            : '$surfaceBackgroundPrimary',
                        transition: 'background-color 0.2s ease-in-out',
                      }}
                    >
                      {(
                        dragHandleProps: SortableItemRenderProps['dragHandleProps'],
                      ) => (
                        <StackLayout
                          role="listitem"
                          orientation="horizontal"
                          verticalAlignment="center"
                          spacing="$threeQuarters"
                          aria-live="polite"
                          css={{
                            width: '100%',
                          }}
                        >
                          {hasArrayMoveControlAnnotation && (
                            <MoveArrayItemsCheckboxContainer
                              isSelectionMode={isSelectionMode}
                              selectedItems={selectedItems}
                              handleToggleSelection={handleToggleSelection}
                              itemLabel={itemLabel}
                              index={index}
                            />
                          )}
                          {isFlowNodeControlAnnotation ? (
                            <FlowNodeArrayItem
                              defaultLabel={defaultItemLabel}
                              itemLabel={itemLabel}
                              itemNumber={itemNumber}
                              itemData={itemData}
                              path={path}
                              handleOnMoveDown={handleOnMoveDown}
                              handleOnMoveUp={handleOnMoveUp}
                              handleRemoveItem={handleRemoveItem}
                              handleDuplicateItem={handleDuplicateItem}
                              onMoveToPosition={handleMoveToPosition}
                              index={index}
                              arrayLength={itemsWithLabels.length}
                              disableActions={disableActions}
                              data={itemsWithLabels}
                              nodeNextKeyMap={nodeNextKeyMap}
                              schema={schema}
                              rootSchema={rootSchema}
                              enabled={enabled}
                              renderers={renderers}
                              cells={cells}
                              combinatorProperties={combinatorProperties}
                              onModalStateChange={handleDrawerStateChange}
                              activeArrayItemIndex={activeDrawerIndex}
                              hasError={arrayItemHasError(index)}
                              dragHandleProps={dragHandleProps}
                            />
                          ) : (
                            <>
                              <Box css={{ flexGrow: 0, flexShrink: 0 }}>
                                <NumberIcon number={itemNumber} />
                              </Box>

                              <Box css={{ flexGrow: 1 }}>
                                <TextAction
                                  as={NavLink}
                                  to={to}
                                  state={locationState}
                                >
                                  {itemLabel}
                                </TextAction>
                              </Box>
                              <Box css={{ flexGrow: 0, flexShrink: 0 }}>
                                <ArrayActionButtons
                                  showDeleteModal
                                  onDuplicate={handleDuplicateItem(
                                    itemData,
                                    index,
                                  )}
                                  onDelete={handleRemoveItem(path, index)}
                                  onMoveDown={handleOnMoveDown(path, index)}
                                  onMoveUp={handleOnMoveUp(path, index)}
                                  onMoveToPosition={handleMoveToPosition(
                                    path,
                                    index,
                                  )}
                                  itemLabel={itemLabel as string}
                                  itemIndex={index}
                                  arrayLength={itemsWithLabels.length}
                                  disableAllActions={disableActions}
                                  dragHandleProps={dragHandleProps}
                                />
                              </Box>
                            </>
                          )}
                        </StackLayout>
                      )}
                    </SortableItem>
                  );
                },
              )}
            </StackLayout>
          </SortableWrapper>
        </>
      )}
      {combinatorActionMenuItems?.length ? (
        <CombinatorArrayAddActionMenu
          buttonLabel={arrayAddLabel}
          menuItems={combinatorActionMenuItems}
          disabled={disableActions}
          onSelectItem={(itemId) => {
            addCombinatorOptionToArray({
              value: itemId,
              combinatorProperties,
              path,
              addItem,
            });
            if (isLocalizable) {
              addLocalizedArrayItem();
            }
          }}
        />
      ) : (
        <ArrayAddButton
          addItem={addItem}
          path={path}
          createDefaultValue={handleCreateDefaultValue}
          addButtonLabel={arrayAddLabel}
          disabled={disableActions}
        />
      )}
    </>
  );
};

const renderArrayControl = (formContentsProps: EntityFormArrayControlProps) => (
  <ArrayOfComplexObjectsControlContents {...formContentsProps} />
);

export const ArrayOfComplexObjectsControl: ArrayControl = (props) => (
  <EntityFormArrayControl {...props} renderControl={renderArrayControl} />
);
